/**
 * 표 상자를 창 아래 끝까지 채우는 높이 (v2.249.0 · 영업진행)
 *
 * 왜: 영업진행 표 상자는 `maxHeight: calc(100vh - 320px)` **고정**이었다. 320 은 「제목 줄 + 도구 줄 + 검색·단계 상자」가
 *   각각 한 줄일 때의 높이라, 위쪽 어디든 한 줄이 늘면 표가 그만큼 창 밖으로 밀려 쪽 전체가 스크롤됐다
 *   (실측 2026-10-14: 1366 폭에서 39px · 1280 폭에서 65px 더 밀려 있었다 — 도구 줄 단추가 두 줄이 되어서).
 *   v2.249.0 에 제목 옆에 [영업 목록 | 판매보고서] 탭 단추가 붙으며 제목 줄도 좁은 화면에서 두 줄이 될 수 있어,
 *   **표의 실제 위치를 재서** 남은 높이를 준다.
 *
 * 무엇을: 표 상자 위쪽 끝(쪽 스크롤 상자 기준 · 스크롤해도 같은 값) → 창 높이 − 위쪽 끝 − reservePx.
 *   reservePx = 0 이면 지금(v2.248.0)과 같이 상자 아래 끝이 창 아래 끝에 닿는다.
 *   다시 재는 때: 창 크기 · 쪽 내용 크기가 바뀔 때(줄바꿈·안내 띠·불러오기) · `active` 가 바뀔 때(숨겨 둔 탭이 다시 보일 때).
 *
 * 폰(768px 미만)은 null — 옛 고정 계산을 그대로 쓴다(위쪽이 원래 길어 쪽 스크롤이 정상인 화면이다).
 * 숨겨 둔 탭(display:none) 안에서는 재지 않는다(위치가 0 으로 나와 엉뚱한 값이 된다).
 */

import { useLayoutEffect, useState, type RefObject } from 'react';

export function useFillHeight(
  ref: RefObject<HTMLElement | null>,
  { reservePx = 0, minPx = 240, active = true }: { reservePx?: number; minPx?: number; active?: boolean } = {},
): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    // 쪽 스크롤 상자 = App 껍데기의 <main overflow-y-auto>. 없으면 창.
    const scroller = el.closest('main');
    const measure = () => {
      if (window.innerWidth < 768) { setHeight(null); return; }
      if (el.offsetParent === null) return; // 숨은 탭
      const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const scrolled = scroller ? scroller.scrollTop : window.scrollY;
      const top = el.getBoundingClientRect().top - viewTop + scrolled;
      const viewH = scroller ? scroller.clientHeight : window.innerHeight;
      const next = Math.max(minPx, Math.floor(viewH - top - reservePx));
      setHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    // 위쪽 줄의 높이가 바뀌면 쪽 내용(첫 자식)의 크기가 바뀐다 — 그것과 스크롤 상자 자체를 본다.
    //   (표 높이를 바꾸면 쪽 내용 크기도 바뀌어 한 번 더 불리지만, 같은 값이면 그대로라 돌고 돌지 않는다)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && scroller) {
      ro.observe(scroller);
      if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [ref, reservePx, minPx, active]);

  return height;
}
